import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader } from "@dl/web-core-js/shell";
import { Card, Segmented, StatTile, Switch, cn } from "@dl/web-core-js/ui";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, Info } from "lucide-react";
import { Fragment, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CASH_KINDS,
  ReplenishmentClient,
  replenishmentKeys,
  type CashKind,
  type CashProjection,
} from "../../api/replenishment";
import { SupplierLink } from "../../components/SupplierLink";
import { NexpimView } from "../../views";
import { paymentTermsLabel } from "./paymentTerms";
import { formatCad } from "./replenishment/shared";

/**
 * What purchasing will pay out, week by week, in CAD.
 *
 * <p>Four kinds of money, from certain to possible: goods already received and
 * not yet due, what issued orders still owe, drafts and orders waiting for
 * approval, and what the latest replenishment run suggests but nobody has
 * drafted. The first two are always counted — they are commitments; the other
 * two can be switched on to see what the plan would cost if it were followed.</p>
 *
 * <p>Nothing is computed here but sums: the server places every amount on its
 * payment day (arrival plus the supplier's terms) and folds them into weeks.</p>
 */

/** Chart tokens, not status colours: a kind is a series, not a verdict. */
const KIND_COLOR: Record<CashKind, string> = {
  RECEIVED: "var(--chart-2)",
  ON_ORDER: "var(--chart-1)",
  PENDING: "var(--chart-3)",
  SUGGESTED: "var(--chart-4)",
};

type Row = CashProjection["rows"][number];
type Grouping = "supplier" | "family";

interface PivotRow {
  key: string;
  label: string;
  sublabel: string | null;
  /** Set on a supplier's row, so its name links to the supplier. */
  supplierId: number | null;
  amounts: number[];
  later: number;
  total: number;
  children: PivotRow[];
}

const SHORT_DATE = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" });

function weekLabel(iso: string): string {
  return SHORT_DATE.format(new Date(`${iso}T00:00:00`));
}

function sum(values: number[]): number {
  return values.reduce((total, value) => total + value, 0);
}

export function CashProjectionPage() {
  const { t } = useTranslation();
  const { http, hasView } = useAuth();
  const allowed = hasView(NexpimView.PURCHASING);
  const client = useMemo(() => new ReplenishmentClient(http), [http]);

  const [withPending, setWithPending] = useState(true);
  const [withSuggested, setWithSuggested] = useState(false);
  const [grouping, setGrouping] = useState<Grouping>("supplier");

  const { data, isLoading, error } = useQuery({
    queryKey: replenishmentKeys.cash,
    queryFn: () => client.cash(),
    enabled: allowed,
    staleTime: 60_000,
  });

  const included = useMemo(
    () =>
      new Set<CashKind>([
        "RECEIVED",
        "ON_ORDER",
        ...(withPending ? (["PENDING"] as const) : []),
        ...(withSuggested ? (["SUGGESTED"] as const) : []),
      ]),
    [withPending, withSuggested],
  );

  const kindLabel = (kind: CashKind) =>
    ({
      RECEIVED: t("cash.kind.received", "Received, not yet due"),
      ON_ORDER: t("cash.kind.on-order", "On order"),
      PENDING: t("cash.kind.pending", "Drafts and awaiting approval"),
      SUGGESTED: t("cash.kind.suggested", "Suggested, not drafted"),
    })[kind];

  if (!allowed) {
    return (
      <>
        <PageHeader title={t("navigation.item.cash-projection", "Cash projection")} />
        <div className="p-4 lg:p-6">
          <p className="text-[13px] text-ink-muted">
            {t("error.access-denied.detail", "Your account does not have permission to view this page.")}
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={t("navigation.item.cash-projection", "Cash projection")}
        subtitle={t("cash.subtitle", "What purchasing will pay, week by week, in Canadian dollars.")}
        actions={
          <div className="flex flex-wrap items-center gap-4">
            <Switch checked={withPending} onChange={setWithPending} label={t("cash.include-pending", "Include drafts")} />
            <Switch
              checked={withSuggested}
              onChange={setWithSuggested}
              label={t("cash.include-suggested", "Include suggestions")}
            />
          </div>
        }
      />
      <div className="flex flex-col gap-4 p-4 lg:p-6">
        {error ? (
          <p className="rounded-md bg-risk-soft px-3 py-2 text-[13px] text-risk">
            {t("cash.error", "The projection could not be loaded.")}
          </p>
        ) : isLoading || !data ? (
          <div className="flex h-40 items-center justify-center">
            <span className="size-5 animate-spin rounded-full border-2 border-accent border-t-transparent" />
          </div>
        ) : (
          <>
            <Tiles data={data} included={included} />
            <Card className="p-4">
              <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
                <h2 className="text-[14px] font-semibold text-ink">{t("cash.by-week", "Payments by week")}</h2>
                <Legend kinds={CASH_KINDS.filter((kind) => included.has(kind))} label={kindLabel} />
              </div>
              <StackedWeeks data={data} included={included} label={kindLabel} />
            </Card>
            <Card className="overflow-hidden">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-4 py-3">
                <h2 className="text-[14px] font-semibold text-ink">{t("cash.detail", "Detail")}</h2>
                <Segmented<Grouping>
                  value={grouping}
                  onChange={setGrouping}
                  options={[
                    { value: "supplier", label: t("cash.by-supplier", "By supplier") },
                    { value: "family", label: t("cash.by-family", "By material family") },
                  ]}
                />
              </div>
              <Pivot data={data} included={included} grouping={grouping} />
            </Card>
            <Explanation />
          </>
        )}
      </div>
    </>
  );
}

/** Next four weeks, the whole horizon, and what falls after it — each split by certainty. */
function Tiles({ data, included }: { data: CashProjection; included: Set<CashKind> }) {
  const { t } = useTranslation();
  const totals = (kinds: CashKind[], weeks: number, withLater: boolean) =>
    data.totals
      .filter((total) => kinds.includes(total.kind) && included.has(total.kind))
      .reduce((acc, total) => acc + sum(total.amounts.slice(0, weeks)) + (withLater ? total.later : 0), 0);
  const laterOnly = (kinds: CashKind[]) =>
    data.totals.filter((total) => kinds.includes(total.kind) && included.has(total.kind)).reduce((acc, total) => acc + total.later, 0);

  const committed: CashKind[] = ["RECEIVED", "ON_ORDER"];
  const split = (value: (kinds: CashKind[]) => number) => {
    const parts = [
      `${t("cash.committed", "committed")} ${formatCad(value(committed))}`,
      included.has("PENDING") ? `${t("cash.pending", "drafts")} ${formatCad(value(["PENDING"]))}` : null,
      included.has("SUGGESTED") ? `${t("cash.suggested", "suggested")} ${formatCad(value(["SUGGESTED"]))}` : null,
    ];
    return parts.filter(Boolean).join(" · ");
  };
  const horizon = data.weeks.length;
  const all: CashKind[] = [...CASH_KINDS];

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      <StatTile
        label={t("cash.next-4-weeks", "Next 4 weeks")}
        value={formatCad(totals(all, 4, false))}
        note={split((kinds) => totals(kinds, 4, false))}
      />
      <StatTile
        label={t("cash.next-weeks", "Next {{count}} weeks", { count: horizon })}
        value={formatCad(totals(all, horizon, false))}
        note={split((kinds) => totals(kinds, horizon, false))}
      />
      <StatTile
        label={t("cash.later", "Later")}
        value={formatCad(laterOnly(all))}
        note={split(laterOnly)}
      />
    </div>
  );
}

function Legend({ kinds, label }: { kinds: CashKind[]; label: (kind: CashKind) => string }) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
      {kinds.map((kind) => (
        <span key={kind} className="inline-flex items-center gap-1.5 text-[12px] text-ink-muted">
          <span className="size-2.5 rounded-sm" style={{ background: KIND_COLOR[kind] }} />
          {label(kind)}
        </span>
      ))}
    </div>
  );
}

/**
 * One column per week, its kinds stacked from certain (bottom) to possible
 * (top). Hand-drawn rather than through SeriesBarChart, which groups bars side
 * by side: a payment week is one total, and side-by-side bars would make the
 * eye add them up.
 */
function StackedWeeks({
  data,
  included,
  label,
}: {
  data: CashProjection;
  included: Set<CashKind>;
  label: (kind: CashKind) => string;
}) {
  const { t } = useTranslation();
  const kinds = CASH_KINDS.filter((kind) => included.has(kind));
  const byKind = new Map(data.totals.map((total) => [total.kind, total]));
  const weekTotals = data.weeks.map((_, week) =>
    kinds.reduce((acc, kind) => acc + Math.max(0, byKind.get(kind)?.amounts[week] ?? 0), 0),
  );
  const top = Math.max(...weekTotals, 1);
  const HEIGHT = 200;

  return (
    <div className="overflow-x-auto">
      <div className="flex min-w-[560px] gap-3">
        <div className="flex w-14 shrink-0 flex-col justify-between pb-5 text-right text-[11px] tabular-nums text-ink-faint" style={{ height: HEIGHT + 20 }}>
          <span>{formatCad(top)}</span>
          <span>{formatCad(top / 2)}</span>
          <span>{formatCad(0)}</span>
        </div>
        <div className="relative flex flex-1 items-end gap-1.5">
          <div className="pointer-events-none absolute inset-x-0 top-0 border-t border-dashed border-line-soft" />
          <div className="pointer-events-none absolute inset-x-0 border-t border-dashed border-line-soft" style={{ top: HEIGHT / 2 }} />
          {data.weeks.map((week, index) => {
            const total = weekTotals[index] ?? 0;
            const tooltip = [
              `${t("cash.week-of", "Week of")} ${weekLabel(week)}: ${formatCad(total)}`,
              ...kinds.map((kind) => `${label(kind)}: ${formatCad(byKind.get(kind)?.amounts[index] ?? 0)}`),
            ].join("\n");
            return (
              <div key={week} className="flex min-w-0 flex-1 flex-col items-center gap-1" title={tooltip}>
                <span className="text-[10px] tabular-nums text-ink-muted">{total > 0 ? formatCad(total) : ""}</span>
                <div className="flex w-full max-w-10 flex-col-reverse overflow-hidden rounded-t" style={{ height: (total / top) * HEIGHT }}>
                  {kinds.map((kind) => {
                    const value = Math.max(0, byKind.get(kind)?.amounts[index] ?? 0);
                    if (value <= 0) return null;
                    return (
                      <div
                        key={kind}
                        style={{
                          height: `${String((value / total) * 100)}%`,
                          background: KIND_COLOR[kind],
                          opacity: kind === "SUGGESTED" ? 0.6 : 1,
                        }}
                      />
                    );
                  })}
                </div>
                <span className={cn("text-[11px] tabular-nums", index === 0 ? "font-semibold text-ink" : "text-ink-faint")}>
                  {index === 0 ? t("cash.this-week", "This week") : weekLabel(week)}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/** Folds the server's rows into groups: supplier → families, or family → suppliers. */
function pivot(
  rows: Row[],
  included: Set<CashKind>,
  grouping: Grouping,
  weekCount: number,
  noFamily: string,
  noSupplier: string,
  termsLabel: (row: Row) => string | null,
): PivotRow[] {
  const outer = new Map<string, PivotRow>();
  const add = (target: PivotRow, row: Row) => {
    row.amounts.forEach((value, index) => {
      target.amounts[index] = (target.amounts[index] ?? 0) + value;
    });
    target.later += row.later;
    target.total += sum(row.amounts) + row.later;
  };
  const blank = (key: string, label: string, sublabel: string | null, supplierId: number | null): PivotRow => ({
    key,
    label,
    sublabel,
    supplierId,
    amounts: Array.from({ length: weekCount }, () => 0),
    later: 0,
    total: 0,
    children: [],
  });
  for (const row of rows) {
    if (!included.has(row.kind)) continue;
    const supplierKey = `s${String(row.supplier_id ?? "none")}`;
    const familyKey = `f${row.part_group_code ?? "none"}`;
    const supplierLabel = row.supplier_name ?? noSupplier;
    const familyLabel = row.part_group_code ? `${row.part_group_code} · ${row.part_group_name ?? ""}` : noFamily;
    const supplierId = row.supplier_id ?? null;
    const [outerKey, outerLabel, outerSub, outerSupplier, innerKey, innerLabel, innerSupplier] =
      grouping === "supplier"
        ? [supplierKey, supplierLabel, termsLabel(row), supplierId, familyKey, familyLabel, null]
        : [familyKey, familyLabel, null, null, supplierKey, supplierLabel, supplierId];
    const group = outer.get(outerKey) ?? blank(outerKey, outerLabel, outerSub, outerSupplier);
    outer.set(outerKey, group);
    let child = group.children.find((entry) => entry.key === innerKey);
    if (!child) {
      child = blank(innerKey, innerLabel, null, innerSupplier);
      group.children.push(child);
    }
    add(group, row);
    add(child, row);
  }
  const ordered = [...outer.values()].filter((group) => Math.abs(group.total) >= 0.005);
  ordered.sort((a, b) => b.total - a.total);
  for (const group of ordered) group.children.sort((a, b) => b.total - a.total);
  return ordered;
}

function Pivot({ data, included, grouping }: { data: CashProjection; included: Set<CashKind>; grouping: Grouping }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState<Set<string>>(new Set());
  const groups = useMemo(
    () =>
      pivot(
        data.rows,
        included,
        grouping,
        data.weeks.length,
        t("cash.no-family", "Expenses and shipping"),
        t("cash.no-supplier", "No supplier"),
        (row) => (row.payment_terms ? paymentTermsLabel(row.payment_terms, row.payment_terms_days, row.deposit_percent, t) : null),
      ),
    [data, included, grouping, t],
  );
  const toggle = (key: string) =>
    setOpen((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const totals = data.weeks.map((_, index) => sum(groups.map((group) => group.amounts[index] ?? 0)));
  const totalLater = sum(groups.map((group) => group.later));

  if (groups.length === 0) {
    return <p className="px-4 py-8 text-center text-[13px] text-ink-muted">{t("cash.empty", "Nothing to pay over the horizon.")}</p>;
  }

  const money = (value: number) => (Math.abs(value) < 0.5 ? <span className="text-ink-faint">·</span> : formatCad(value));

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[1100px] border-collapse text-[12px] tabular-nums">
        <thead>
          <tr className="border-b border-line bg-surface-sunk text-[11px] uppercase tracking-wide text-ink-faint">
            <th className="sticky left-0 z-10 bg-surface-sunk px-3 py-2 text-left font-medium">
              {grouping === "supplier" ? t("cash.supplier", "Supplier") : t("cash.family", "Material family")}
            </th>
            {data.weeks.map((week, index) => (
              <th key={week} className="whitespace-nowrap px-2 py-2 text-right font-medium">
                {index === 0 ? t("cash.this-week", "This week") : weekLabel(week)}
              </th>
            ))}
            <th className="whitespace-nowrap px-2 py-2 text-right font-medium">{t("cash.later", "Later")}</th>
            <th className="whitespace-nowrap px-3 py-2 text-right font-medium">{t("cash.total", "Total")}</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((group) => {
            const expanded = open.has(group.key);
            return (
              <Fragment key={group.key}>
                <tr
                  className="cursor-pointer border-b border-line-soft hover:bg-surface-sunk/60 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent"
                  tabIndex={0}
                  aria-expanded={expanded}
                  onClick={() => toggle(group.key)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      toggle(group.key);
                    }
                  }}
                >
                  {/* One line per supplier: a long name is cut, not wrapped, so the rows keep one height. */}
                  <td className="sticky left-0 z-10 max-w-72 bg-surface px-3 py-2 text-left" title={group.label}>
                    <span className="flex min-w-0 items-center gap-1 whitespace-nowrap font-medium text-ink">
                      {expanded ? <ChevronDown className="size-3.5 shrink-0" /> : <ChevronRight className="size-3.5 shrink-0" />}
                      <SupplierLink id={group.supplierId} className="truncate">
                        {group.label}
                      </SupplierLink>
                      {group.sublabel && <span className="ml-1 shrink-0 text-[11px] font-normal text-ink-faint">{group.sublabel}</span>}
                    </span>
                  </td>
                  {group.amounts.map((value, index) => (
                    <td key={index} className="px-2 py-2 text-right text-ink">
                      {money(value)}
                    </td>
                  ))}
                  <td className="px-2 py-2 text-right text-ink-muted">{money(group.later)}</td>
                  <td className="px-3 py-2 text-right font-semibold text-ink">{formatCad(group.total)}</td>
                </tr>
                {expanded &&
                  group.children.map((child) => (
                    <tr key={`${group.key}-${child.key}`} className="border-b border-line-soft bg-surface-sunk/30">
                      <td className="sticky left-0 z-10 max-w-72 truncate whitespace-nowrap bg-surface-sunk px-3 py-1.5 pl-9 text-left text-ink-muted" title={child.label}>
                        <SupplierLink id={child.supplierId}>{child.label}</SupplierLink>
                      </td>
                      {child.amounts.map((value, index) => (
                        <td key={index} className="px-2 py-1.5 text-right text-ink-muted">
                          {money(value)}
                        </td>
                      ))}
                      <td className="px-2 py-1.5 text-right text-ink-faint">{money(child.later)}</td>
                      <td className="px-3 py-1.5 text-right text-ink-muted">{formatCad(child.total)}</td>
                    </tr>
                  ))}
              </Fragment>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-line bg-surface-sunk font-semibold text-ink">
            <td className="sticky left-0 z-10 bg-surface-sunk px-3 py-2 text-left">{t("cash.total", "Total")}</td>
            {totals.map((value, index) => (
              <td key={index} className="px-2 py-2 text-right">
                {money(value)}
              </td>
            ))}
            <td className="px-2 py-2 text-right">{money(totalLater)}</td>
            <td className="px-3 py-2 text-right">{formatCad(sum(totals) + totalLater)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

/** The rule in four lines, so nobody has to guess why a payment sits in week 5. */
function Explanation() {
  const { t } = useTranslation();
  return (
    <Card className="flex gap-3 p-4">
      <Info className="mt-0.5 size-4 shrink-0 text-info" />
      <div className="flex flex-col gap-1 text-[12px] leading-relaxed text-ink-muted">
        <p className="font-medium text-ink">{t("cash.rule.title", "How a payment lands in a week")}</p>
        <p>
          {t(
            "cash.rule.arrival",
            "Payment day = the day the goods arrive + the supplier's payment terms (the purchasing settings' default when the supplier has none).",
          )}
        </p>
        <p>
          {t(
            "cash.rule.date",
            "Arrival is the line's promised date, else its required date, else the order's, else the day it went out plus the lead time. A date already past counts as today: a late delivery is still to come, and still to pay, unless it is past the stale-order days in the purchasing settings, when nobody expects it any more and it is left out.",
          )}
        </p>
        <p>
          {t(
            "cash.rule.received",
            "Goods already received count from their receipt day and drop off once due; nexpim does not record payments. Suggestions land at today plus the lead time the run planned with. All amounts are converted to CAD at the current exchange rates.",
          )}
        </p>
      </div>
    </Card>
  );
}
